import { Fragment, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarIcon, ChevronDownIcon } from "@heroicons/react/outline";
import moment from "moment";

const API_FORMAT = "YYYY-MM-DD";

export const DATE_PRESETS = [
  {
    key: "this_month",
    label: "This Month",
    range: () => [moment().startOf("month"), moment()],
  },
  {
    key: "last_month",
    label: "Last Month",
    range: () => [
      moment().subtract(1, "month").startOf("month"),
      moment().subtract(1, "month").endOf("month"),
    ],
  },
  {
    key: "last_3_months",
    label: "Last 3 Months",
    range: () => [moment().subtract(3, "months"), moment()],
  },
];

/** { key, start_date, end_date } for a preset, dates as YYYY-MM-DD */
export function presetRange(key) {
  const preset = DATE_PRESETS.find((p) => p.key === key) || DATE_PRESETS[0];
  const [start, end] = preset.range();
  return {
    key: preset.key,
    start_date: start.format(API_FORMAT),
    end_date: end.format(API_FORMAT),
  };
}

/**
 * Full-width card: selected dates on the left, period picker on the right.
 * @param {{key: string, start_date: string, end_date: string}} value
 * @param {(value) => void} onChange
 */
export default function RevenueDateFilter({ value, onChange }) {
  const [customStart, setCustomStart] = useState(value?.start_date || "");
  const [customEnd, setCustomEnd] = useState(value?.end_date || "");

  const label =
    value?.key === "custom"
      ? "Custom"
      : DATE_PRESETS.find((p) => p.key === value?.key)?.label || "This Month";

  const today = moment().format(API_FORMAT);
  const customValid =
    customStart && customEnd && moment(customStart).isSameOrBefore(customEnd);

  return (
    <div className="w-full flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-[#121212] border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 mb-6">
      {/* Left: selected dates */}
      <div className="min-w-0">
        <p className="text-xs text-[#A3AED0] dark:text-[#BCBCBC]">Showing</p>
        <p className="font-semibold text-[#2B3674] dark:text-white">
          {moment(value?.start_date).format("DD MMM YYYY")} –{" "}
          {moment(value?.end_date).format("DD MMM YYYY")}
        </p>
      </div>

      {/* Right: period picker */}
      <Popover className="relative w-full sm:w-auto">
        {({ close }) => (
          <>
            <Popover.Button className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-2 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary">
              <CalendarIcon className="w-5 h-5 text-primary" aria-hidden="true" />
              <span className="flex-1 text-left">{label}</span>
              <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute left-0 sm:left-auto sm:right-0 z-20 mt-2 w-full sm:w-72 rounded-md bg-white dark:bg-[#1F1F1F] shadow-lg ring-1 ring-black ring-opacity-5 p-2">
                {DATE_PRESETS.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => {
                      onChange(presetRange(preset.key));
                      close();
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                      value?.key === preset.key
                        ? "bg-primary text-white"
                        : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}

                <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-3 px-1">
                  <p
                    className={`text-sm font-medium mb-2 ${
                      value?.key === "custom"
                        ? "text-primary"
                        : "text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    Custom
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      From
                      <input
                        type="date"
                        value={customStart}
                        max={customEnd || today}
                        onChange={(e) => setCustomStart(e.target.value)}
                        className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-transparent dark:text-white"
                      />
                    </label>
                    <label className="text-xs text-gray-500 dark:text-gray-400">
                      To
                      <input
                        type="date"
                        value={customEnd}
                        min={customStart || undefined}
                        max={today}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        className="mt-1 w-full border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-transparent dark:text-white"
                      />
                    </label>
                  </div>
                  <button
                    type="button"
                    disabled={!customValid}
                    onClick={() => {
                      onChange({
                        key: "custom",
                        start_date: customStart,
                        end_date: customEnd,
                      });
                      close();
                    }}
                    className={`mt-3 w-full rounded-md py-2 text-sm text-white ${
                      customValid
                        ? "bg-primary"
                        : "bg-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Apply
                  </button>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
}
